import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Heart, Check, QrCode, Download, Ruler, ImagePlus, Share2 } from "lucide-react";
import api, { API, mediaUrl, SITE_URL, qrTrackUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import JsonLd from "@/components/store/JsonLd";
import { Stars, StarInput } from "@/components/store/Stars";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const { settings } = useSettings();
  const brandMark = mediaUrl(settings?.page_images?.brand_mark || "https://static.prod-images.emergentagent.com/jobs/65e76132-927f-451c-9933-4dc061721808/images/a347cbd409dd11d18d240db6cd3998b76442cc5a1a337d2840411b46446ca9a6.jpeg");
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [sizeGuide, setSizeGuide] = useState(false);
  const [alertSize, setAlertSize] = useState(null);
  const [alertEmail, setAlertEmail] = useState("");
  const [showQR, setShowQR] = useState(false);
  const buyDone = useRef(false);
  const [reviews, setReviews] = useState({ count: 0, average: 0, reviews: [] });
  const [rf, setRf] = useState({ author_name: "", rating: 0, title: "", body: "", email: "", photos: [] });
  const photoRef = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => { setProduct(data); }).catch(() => setProduct(false));
    api.get(`/products/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.get(`/share/count/product/${slug}`).then(({ data }) => setShareCount(data.count || 0)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!product || buyDone.current) return;
    const buy = searchParams.get("buy");
    if (!buy) return;
    buyDone.current = true;
    const v = (product.variants || []).find((x) => x.size === buy);
    if (v && v.stock > 0) {
      setSize(buy);
      addItem(product, buy, 1);
      toast.success(`${product.title} (${buy}) added to your bag.`);
    } else if (v) {
      setAlertSize(buy);
    }
  }, [product]); // eslint-disable-line

  if (product === null) return <div className="py-32 text-center font-heading text-3xl text-ink/40">Loading…</div>;
  if (product === false) return <div className="py-32 text-center font-heading text-3xl">Piece not found.</div>;

  const isAdmin = user && ["super_admin", "store_manager"].includes(user.role);
  const productUrl = `${window.location.origin}/product/${product.slug}`;
  const shareUrl = `${SITE_URL}/product/${product.slug}`;
  const sharePdp = async () => {
    const link = `${window.location.origin}/api/share/product/${product.slug}`;
    api.post("/share/track", { kind: "product", slug: product.slug }).then(({ data }) => setShareCount(data.count)).catch(() => {});
    if (navigator.share) {
      try { await navigator.share({ title: product.title, text: `${product.title} · Sew Voyage`, url: link }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Copied! Share link is on your clipboard.");
    } catch { window.prompt("Copy this share link:", link); }
  };
  const variantFor = (s) => (product.variants || []).find((v) => v.size === s);

  const inStock = (product.variants || []).some((v) => (v.stock || 0) > 0);
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description || product.fabric || product.title,
    image: (product.images || []).map((i) => mediaUrl(i)).filter(Boolean),
    sku: product.slug,
    brand: { "@type": "Brand", name: "Sew Voyage" },
    category: product.category_slug || undefined,
    offers: {
      "@type": "Offer",
      url: productUrl,
      priceCurrency: "USD",
      price: (product.price || 0).toFixed(2),
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Sew Voyage" },
    },
  };

  if (reviews.count > 0) {
    productLd.aggregateRating = { "@type": "AggregateRating", ratingValue: reviews.average, reviewCount: reviews.count, bestRating: 5 };
    productLd.review = reviews.reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
      author: { "@type": "Person", name: r.author_name },
      name: r.title || undefined,
      reviewBody: r.body || undefined,
      datePublished: (r.created_at || "").slice(0, 10),
    }));
  }

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${window.location.origin}/shop` },
      { "@type": "ListItem", position: 3, name: product.title, item: productUrl },
    ],
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rf.rating) return toast.error("Please select a star rating.");
    if (!rf.author_name.trim()) return toast.error("Please add your name.");
    try {
      await api.post(`/products/${slug}/reviews`, rf);
      toast.success("Thank you — your review will appear once approved.");
      setRf({ author_name: "", rating: 0, title: "", body: "", email: "", photos: [] });
    } catch { toast.error("Could not submit review."); }
  };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/reviews/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRf((s) => ({ ...s, photos: [...s.photos, data.url] }));
    } catch { toast.error("Photo upload failed."); }
    setPhotoBusy(false);
    e.target.value = "";
  };

  const add = () => {
    if (!size) { toast.error("Please select a size."); return; }
    const v = variantFor(size);
    if (!v || v.stock <= 0) { toast.error("That size is sold out."); return; }
    addItem(product, size, qty);
    toast.success("Added to your bag.", { icon: <img src={brandMark} alt="" className="w-5 h-5 object-contain mix-blend-multiply" /> });
  };

  const notifyMe = async () => {
    if (!alertEmail) return;
    try { await api.post("/stock-alert", { email: alertEmail, product_id: product.id, size: alertSize }); toast.success("We'll email you when it's back."); setAlertSize(null); setAlertEmail(""); }
    catch { toast.error("Could not save your request."); }
  };

  return (
    <div className="px-6 md:px-12 py-12" data-testid="product-detail-page">
      <JsonLd id="ld-product" data={productLd} />
      <JsonLd id="ld-breadcrumb" data={breadcrumbLd} />
      <nav className="font-body text-[0.65rem] uppercase tracking-wide2 text-ink/50 mb-8"><Link to="/shop" className="hover:text-ink">Shop</Link> / {product.title}</nav>
      <div className="mx-auto max-w-[1200px] grid md:grid-cols-2 gap-12">
        <div className="bg-cream overflow-hidden"><img src={mediaUrl(product.images?.[0])} alt={product.title} className="w-full object-cover" data-testid="product-main-image" /></div>

        <div className="md:py-4">
          {product.batch_limit && <span className="inline-block bg-warm/50 font-body uppercase tracking-luxe text-[0.6rem] px-3 py-1 mb-4">{product.batch_limit}</span>}
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight" data-testid="product-title">{product.title}</h1>
          <div className="flex items-center gap-3 mt-3 font-body">
            <span className="text-lg" data-testid="product-price">${product.price?.toFixed(2)}</span>
            {product.compare_at_price && <span className="line-through text-ink/40 text-sm">${product.compare_at_price.toFixed(2)}</span>}
          </div>
          {reviews.count > 0 && (
            <a href="#reviews" className="inline-flex items-center gap-2 mt-2 text-ink/70 hover:text-ink transition-colors" data-testid="product-rating-summary"><Stars value={reviews.average} /> <span className="font-body text-xs">{reviews.average} · {reviews.count} review{reviews.count !== 1 ? "s" : ""}</span></a>
          )}
          <p className="font-body text-sm text-ink/70 leading-relaxed mt-6">{product.description}</p>

          {/* Size matrix */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-3">
              <p className="eyebrow">Select Size</p>
              <button onClick={() => setSizeGuide(true)} className="font-body text-[0.65rem] uppercase tracking-wide2 text-ink/60 hover:text-ink flex items-center gap-1" data-testid="size-guide-btn"><Ruler size={13} strokeWidth={1.5} /> Size Guide</button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {(product.variants || []).map((v) => {
                const out = v.stock <= 0;
                return (
                  <button key={v.size} onClick={() => out ? setAlertSize(v.size) : setSize(v.size)}
                          data-testid={`size-option-${v.size}`}
                          className={`w-14 h-12 border font-body text-sm transition-colors relative ${size === v.size ? "bg-ink text-linen border-ink" : "border-ink/30 hover:border-ink"} ${out ? "opacity-40 line-through cursor-pointer" : ""}`}>{v.size}</button>
                );
              })}
            </div>
            {alertSize && (
              <div className="mt-4 border hairline p-4 bg-cream" data-testid="back-in-stock-box">
                <p className="font-body text-sm mb-2">Notify me when <b>{alertSize}</b> is back in stock:</p>
                <div className="flex gap-2">
                  <input value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="Email address" className="input-luxe flex-1" data-testid="back-in-stock-email" />
                  <button onClick={notifyMe} className="btn-primary" data-testid="back-in-stock-submit">Notify Me</button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-8">
            <div className="flex items-center border hairline">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-3">−</button>
              <span className="px-4 font-body text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-3">+</button>
            </div>
            <button onClick={add} className="btn-primary flex-1" data-testid="add-to-cart-btn">Add to Bag</button>
            <button onClick={() => toggle(product.id)} className="w-12 h-12 border border-ink/30 flex items-center justify-center hover:border-ink transition-colors" data-testid="pdp-wishlist-btn">
              <Heart size={18} className={has(product.id) ? "fill-ink text-ink" : "text-ink"} strokeWidth={1.5} />
            </button>
            <button onClick={sharePdp} className="w-12 h-12 border border-ink/30 flex items-center justify-center hover:border-ink transition-colors relative" data-testid="pdp-share-btn" aria-label="Share this piece" title={shareCount > 0 ? `Shared ${shareCount} time${shareCount !== 1 ? "s" : ""}` : "Share"}>
              {copied ? <Check size={18} className="text-ink" strokeWidth={2} /> : <Share2 size={18} className="text-ink" strokeWidth={1.5} />}
              {shareCount > 0 && <span className="absolute -top-2 -right-2 bg-ink text-linen text-[0.55rem] leading-none min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1" data-testid="pdp-share-count">{shareCount}</span>}
            </button>
          </div>

          {/* Details */}
          <div className="mt-10 divide-y hairline border-t border-b">
            <Detail label="Fabric & Care">{product.fabric}<br/>{product.care}</Detail>
            <Detail label="Eco Impact"><span className="flex items-center gap-2"><Check size={14} strokeWidth={1.5} />{product.eco_savings}</span></Detail>
            <Detail label="Model & Fit">{product.model_specs}</Detail>
          </div>

          <div className="mt-8 border hairline p-4 bg-cream" data-testid="product-qr-section">
            <div className="flex items-center gap-4">
              <img src={`${API}/qr?data=${encodeURIComponent(qrTrackUrl(shareUrl, `product:${product.slug}`))}&fill=1a1a1a&bg=transparent&logo=1`} alt={`Scan to view ${product.title}`} className="w-28 h-28" data-testid="product-qr-image" />
              <div>
                <p className="eyebrow mb-1">Scan to View</p>
                <p className="font-body text-xs text-ink/60 leading-relaxed mb-3 max-w-[15rem]">Point your camera here to open this piece on your phone — or save the code to share it.</p>
                <a href={`${API}/qr?data=${encodeURIComponent(qrTrackUrl(shareUrl, `product:${product.slug}`))}&fill=1a1a1a&logo=1`} download={`${product.slug}-qr.png`} className="btn-outline text-[0.65rem] inline-flex items-center gap-2" data-testid="product-qr-download"><Download size={14} /> Download QR</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section id="reviews" className="mx-auto max-w-[1200px] mt-20 border-t hairline pt-12 scroll-mt-24" data-testid="reviews-section">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <p className="eyebrow mb-3">Reviews</p>
            <h2 className="font-heading text-4xl tracking-tight">{reviews.average || "—"}<span className="text-ink/40 text-xl"> / 5</span></h2>
            <Stars value={reviews.average} size={18} className="mt-2" />
            <p className="font-body text-sm text-ink/50 mt-2">{reviews.count} review{reviews.count !== 1 ? "s" : ""}</p>
            <form onSubmit={submitReview} className="mt-8 space-y-3" data-testid="review-form">
              <p className="eyebrow">Write a Review</p>
              <StarInput value={rf.rating} onChange={(n) => setRf((s) => ({ ...s, rating: n }))} />
              <input className="input-luxe w-full" placeholder="Your name" value={rf.author_name} onChange={(e) => setRf((s) => ({ ...s, author_name: e.target.value }))} data-testid="review-name" />
              <input className="input-luxe w-full" type="email" placeholder="Your email (for Verified Buyer badge)" value={rf.email} onChange={(e) => setRf((s) => ({ ...s, email: e.target.value }))} data-testid="review-email" />
              <input className="input-luxe w-full" placeholder="Title (optional)" value={rf.title} onChange={(e) => setRf((s) => ({ ...s, title: e.target.value }))} data-testid="review-title" />
              <textarea className="input-luxe w-full" rows={3} placeholder="Share your thoughts…" value={rf.body} onChange={(e) => setRf((s) => ({ ...s, body: e.target.value }))} data-testid="review-body" />
              {rf.photos.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="review-photo-previews">
                  {rf.photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16">
                      <img src={mediaUrl(p)} alt="" className="w-full h-full object-cover border hairline" />
                      <button type="button" onClick={() => setRf((s) => ({ ...s, photos: s.photos.filter((_, j) => j !== i) }))} className="absolute -top-2 -right-2 bg-ink text-linen w-5 h-5 rounded-full text-xs leading-none" aria-label="Remove photo">×</button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => photoRef.current?.click()} className="adm-btn-outline w-full" data-testid="review-add-photo"><ImagePlus size={13} /> {photoBusy ? "Uploading…" : "Add Photo"}</button>
              <p className="font-body text-[0.6rem] text-ink/40 -mt-1">Recommended: square (1:1), at least 800×800px · JPG or PNG up to 8MB</p>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={addPhoto} data-testid="review-photo-file" />
              <button className="btn-primary w-full" data-testid="review-submit">Submit Review</button>
            </form>
          </div>
          <div className="md:col-span-2 space-y-6">
            {reviews.reviews.length === 0 && <p className="font-body text-sm text-ink/40" data-testid="reviews-empty">No reviews yet — be the first to share your thoughts.</p>}
            {reviews.reviews.map((r) => (
              <div key={r.id} className="border-b hairline pb-6" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className="font-body text-[0.65rem] uppercase tracking-wide2 text-ink/40">{(r.created_at || "").slice(0, 10)}</span>
                </div>
                {r.title && <p className="font-heading text-lg mt-2">{r.title}</p>}
                {r.body && <p className="font-body text-sm text-ink/70 leading-relaxed mt-1">{r.body}</p>}
                {r.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-photos-${r.id}`}>
                    {r.photos.map((p, i) => <img key={i} src={mediaUrl(p)} alt="Customer photo" loading="lazy" className="w-20 h-20 object-cover border hairline" />)}
                  </div>
                )}
                <p className="font-body text-[0.7rem] uppercase tracking-wide2 text-ink/50 mt-3">— {r.author_name}{r.verified ? " · Verified Buyer" : ""}</p>
                {r.reply && (
                  <div className="mt-3 ml-4 pl-4 border-l-2 border-ink/20" data-testid={`review-reply-${r.id}`}>
                    <p className="font-body text-[0.65rem] uppercase tracking-wide2 text-ink/50 mb-1">Sew Voyage replied</p>
                    <p className="font-body text-sm text-ink/70 italic leading-relaxed">{r.reply}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {sizeGuide && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6" onClick={() => setSizeGuide(false)} data-testid="size-guide-modal">
          <div className="bg-linen max-w-md w-full p-8 border hairline" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-3xl mb-4">Size Guide</h3>
            <p className="font-body text-sm text-ink/70 leading-relaxed">{product.size_guide}</p>
            <p className="font-body text-sm text-ink/70 mt-4">{product.model_specs}</p>
            <button onClick={() => setSizeGuide(false)} className="btn-primary mt-6 w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex justify-between items-center font-body uppercase tracking-wide2 text-[0.7rem]">
        {label}<span className="text-ink/40 text-lg">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="font-body text-sm text-ink/70 leading-relaxed pt-3">{children}</div>}
    </div>
  );
}
